import type { Category, CalculatorMeta } from '@/types/calculator'
import type { LocaleCode } from './locales'

interface LocalizedCalculatorOverride {
  name: string
  description: string
  seoTitle: string
  seoDescription: string
}

const ES_ELECTRICAL_OVERRIDES: Record<string, LocalizedCalculatorOverride> = {
  'ohms-law': {
    name: 'Calculadora de la Ley de Ohm',
    description: 'Calcula voltaje, corriente, resistencia y potencia usando la Ley de Ohm.',
    seoTitle: 'Calculadora de la Ley de Ohm - Voltaje, Corriente, Resistencia y Potencia',
    seoDescription: 'Calculadora gratuita de Ley de Ohm. Calcula voltaje (V), corriente (A), resistencia (Ω) y potencia (W) al instante.',
  },
  'voltage-divider': {
    name: 'Calculadora de Divisor de Voltaje',
    description: 'Calcula voltaje de salida, corriente del divisor y disipacion de potencia en un divisor resistivo.',
    seoTitle: 'Calculadora de Divisor de Voltaje - Vout, Corriente y Potencia',
    seoDescription: 'Calcula el voltaje de salida de un divisor de voltaje con resistencias. Introduce Vin, R1 y R2 para obtener Vout y perdidas.',
  },
  'electrical-power': {
    name: 'Calculadora de Potencia Electrica',
    description: 'Calcula potencia electrica usando P=VI, P=I²R o P=V²/R.',
    seoTitle: 'Calculadora de Potencia Electrica - Watts, Volts, Amps y Ohms',
    seoDescription: 'Calcula potencia electrica (W, kW) desde voltaje y corriente, corriente y resistencia, o voltaje y resistencia.',
  },
  resistor: {
    name: 'Calculadora de Resistencias',
    description: 'Calcula resistencia, voltaje, corriente y potencia en circuitos resistivos.',
    seoTitle: 'Calculadora de Resistencias - Resistencia, Voltaje, Corriente y Potencia',
    seoDescription: 'Calcula valores de resistencia, caida de voltaje, corriente y disipacion de potencia.',
  },
  'resistor-color-code': {
    name: 'Calculadora de Codigo de Colores de Resistencia',
    description: 'Decodifica codigos de colores de 4, 5 y 6 bandas para obtener resistencia y tolerancia.',
    seoTitle: 'Calculadora de Codigo de Colores - Decodificador 4, 5 y 6 Bandas',
    seoDescription: 'Decodifica codigos de colores de resistencias al instante. Incluye valor, tolerancia y coeficiente termico.',
  },
  'rc-time-constant': {
    name: 'Calculadora de Constante de Tiempo RC',
    description: 'Calcula constante de tiempo RC, tiempo de carga y porcentajes de descarga.',
    seoTitle: 'Calculadora de Constante de Tiempo RC - Carga y Descarga',
    seoDescription: 'Calcula la constante de tiempo RC (τ = RC), y tiempos de carga y descarga del capacitor.',
  },
  'voltage-drop': {
    name: 'Calculadora de Caida de Voltaje',
    description: 'Calcula caida de voltaje y perdida de potencia en cables electricos.',
    seoTitle: 'Calculadora de Caida de Voltaje - Perdida en Cableado',
    seoDescription: 'Calcula la caida de voltaje en cables electricos con corriente, longitud y seccion del conductor.',
  },
  impedance: {
    name: 'Calculadora de Impedancia',
    description: 'Calcula impedancia de circuitos AC a partir de resistencia y reactancia.',
    seoTitle: 'Calculadora de Impedancia - Impedancia AC Z = √(R² + X²)',
    seoDescription: 'Calcula la magnitud de impedancia en AC desde resistencia y reactancia.',
  },
  'led-resistor': {
    name: 'Calculadora de Resistencia para LED',
    description: 'Calcula el valor correcto de resistencia limitadora de corriente para un circuito LED.',
    seoTitle: 'Calculadora de Resistencia para LED - Resistencia Limitadora',
    seoDescription: 'Calcula la resistencia necesaria para alimentar un LED de forma segura.',
  },
  'resistor-network': {
    name: 'Calculadora de Redes de Resistencias',
    description: 'Calcula resistencia total para resistencias en serie y en paralelo.',
    seoTitle: 'Calculadora de Redes de Resistencias - Serie y Paralelo',
    seoDescription: 'Calcula resistencia total para redes de resistencias en serie o paralelo.',
  },
  'capacitor-energy': {
    name: 'Calculadora de Energia en Capacitor',
    description: 'Calcula energia almacenada en un capacitor (E = 1/2CV²) y carga (Q = CV).',
    seoTitle: 'Calculadora de Energia en Capacitor - E = 1/2CV²',
    seoDescription: 'Calcula energia almacenada en un capacitor y su carga electrica.',
  },
  transformer: {
    name: 'Calculadora de Relacion de Espiras de Transformador',
    description: 'Calcula relacion de espiras, voltaje secundario, corriente secundaria y potencia.',
    seoTitle: 'Calculadora de Transformador - Relacion de Espiras',
    seoDescription: 'Calcula relacion de espiras y valores secundarios para transformadores elevadores y reductores.',
  },
  'power-factor': {
    name: 'Calculadora de Factor de Potencia',
    description: 'Calcula potencia aparente (kVA), real (kW), reactiva (kVAR) y factor de potencia.',
    seoTitle: 'Calculadora de Factor de Potencia - kVA, kW, kVAR y FP',
    seoDescription: 'Calcula factor de potencia y potencias aparente, real y reactiva para circuitos AC.',
  },
  'three-phase-power': {
    name: 'Calculadora de Potencia Trifasica',
    description: 'Calcula potencia trifasica, corriente y kVA desde voltaje y factor de potencia.',
    seoTitle: 'Calculadora de Potencia Trifasica - kW, kVA y Corriente',
    seoDescription: 'Calcula potencia trifasica (kW), potencia aparente (kVA) y corriente de linea.',
  },
}

const ES_CATEGORY_LABELS: Partial<Record<Category, string>> = {
  electrical: 'Electrica',
  energy: 'Energia',
  mechanical: 'Mecanica',
  thermal: 'Termica',
  physics: 'Fisica',
  converters: 'Convertidores',
  tools: 'Herramientas',
  generators: 'Generadores',
  math: 'Matematicas',
  finance: 'Finanzas',
  health: 'Salud',
  everyday: 'Diario',
  science: 'Ciencia',
  environmental: 'Ambiental',
}

const ES_GROUP_CTA: Record<string, string> = {
  calculators: 'Abrir calculadora ->',
  converters: 'Abrir convertidor ->',
  tools: 'Abrir herramienta ->',
  generators: 'Abrir generador ->',
}

const SHELL_LABELS_ES = {
  home: 'Inicio',
  relatedCalculators: 'Calculadoras Relacionadas',
}

export function getLocalizedCalculatorMeta(locale: LocaleCode, calc: CalculatorMeta): CalculatorMeta {
  if (locale === 'es' && calc.category === 'electrical') {
    const override = ES_ELECTRICAL_OVERRIDES[calc.slug]
    if (override) {
      return {
        ...calc,
        name: override.name,
        description: override.description,
        seoTitle: override.seoTitle,
        seoDescription: override.seoDescription,
      }
    }
  }

  return calc
}

export function getLocalizedCategoryLabel(locale: LocaleCode, category: Category, fallbackLabel: string): string {
  if (locale === 'es') {
    return ES_CATEGORY_LABELS[category] ?? fallbackLabel
  }

  return fallbackLabel
}

export function getLocalizedGroupCta(locale: LocaleCode, group: string): string | undefined {
  if (locale === 'es') {
    return ES_GROUP_CTA[group]
  }

  return undefined
}

export function getShellLabels(locale: LocaleCode): { home: string; relatedCalculators: string } {
  if (locale === 'es') {
    return SHELL_LABELS_ES
  }

  return {
    home: 'Home',
    relatedCalculators: 'Related Calculators',
  }
}
